import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Dialog, ModalMask } from '@fx-ui/fine-design';
import { assign, ECode, EMPTY_FN, get, loadScript } from '@libs/shared';
import { globalState } from '../../models';
import { EXTERNAL_URL, platformIntl } from '../../constants';
import { LOCALE_KEY } from '@fx-ui/intl';
import { getErrorMsg, post } from '../../utils';

interface TurnstileDialogProps {
    onClose: () => void;
    onSuccess: () => void;
    data: any;
    url: string;
    visible: boolean;
}

const getLanguage = () => {
    // https://developers.cloudflare.com/turnstile/reference/supported-languages/
    const languageMap: Record<LOCALE_KEY, string> = {
        [LOCALE_KEY.ZH_CN]: 'zh',
        [LOCALE_KEY.ZH_TW]: 'zh-tw',
        [LOCALE_KEY.EN_US]: 'en',
        [LOCALE_KEY.JA_JP]: 'ja',
        [LOCALE_KEY.VI_VN]: 'vi',
        [LOCALE_KEY.TH_TH]: 'th',
        [LOCALE_KEY.ID_ID]: 'id',
        [LOCALE_KEY.KO_KR]: 'ko',
        [LOCALE_KEY.DE_DE]: 'de',
        [LOCALE_KEY.ES_ES]: 'es',
        [LOCALE_KEY.FR_FR]: 'fr',
        [LOCALE_KEY.PT_PT]: 'pt',
        [LOCALE_KEY.RU_RU]: 'ru',
        [LOCALE_KEY.KM_KH]: 'en', // cloudflare 不支持高棉语，暂时使用英语
        [LOCALE_KEY.LO_LA]: 'en',
        [LOCALE_KEY.HI_IN]: 'hi',
        [LOCALE_KEY.BN_BD]: 'en',
        [LOCALE_KEY.TA_IN]: 'en',
        [LOCALE_KEY.UZ_UZ]: 'en',
        [LOCALE_KEY.MN_MN]: 'en',
        [LOCALE_KEY.IT_IT]: 'it',
        [LOCALE_KEY.NL_NL]: 'nl',
        [LOCALE_KEY.PL_PL]: 'pl',
        [LOCALE_KEY.HU_HU]: 'hu',
        [LOCALE_KEY.PT_BR]: 'pt-br'
    };
    const currentLocale = globalState.localeKey!;
    return languageMap[currentLocale] || currentLocale;
};

const TurnstileDialog: React.FC<TurnstileDialogProps> = (props) => {
    const { visible, onClose, onSuccess, data, url } = props;

    const [errorMsg, setErrorMsg] = useState<string>();

    const isMobile = globalState.isMobile;

    useEffect(() => {
        if ((window as any).turnstile) {
            // 已经加载过
            initTurnstile();
        } else {
            (window as any).onloadTurnstileCallback = function () {
                initTurnstile();
            };
        }

        loadScript(
            EXTERNAL_URL.CLOUDFLARE_TURNSTILE_URL + '?onload=onloadTurnstileCallback',
            EMPTY_FN
        );
        setErrorMsg(undefined);

        return () => {
            (window as any).onloadTurnstileCallback = null;
        };
    }, []);

    const initTurnstile = () => {
        (window as any).turnstile.render('#turnstile-wrapper', {
            sitekey: globalState.options?.captcha?.params?.site_key,
            size: isMobile ? 'compact' : 'normal',
            theme: 'light',
            language: getLanguage(),
            callback: (token: string) => {
                doVerify(token);
            },
            'error-callback': (errorCode: number) => {
                // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
                console.error(errorCode);
            }
        });
    };

    const doVerify = (token: string) => {
        post(
            url,
            assign({}, data, {
                captcha_token: token
            }),
            {
                customHandleErrorCodes: [
                    ECode.EMAIL_FORMAT_IS_INVALID,
                    ECode.USER_PHONE_FORMAT_INVALID,
                    ECode.USER_PHONE_REQUIRED,
                    ECode.USER_WRONG_AUTH_CODE_CAPTCHA,
                    ECode.USER_VERIFY_CODE_SEND_LIMIT_EXCEEDED,
                    ECode.USER_PHONE_INVALID,
                    ECode.USER_EMAIL_IS_INCORRECT,
                    ECode.USER_NO_AUTH_CODE_CAPTCHA
                ]
            }
        )
            .then(() => {
                onSuccess();
            })
            .catch((res) => {
                const err = get(res, 'response.data');
                setErrorMsg(getErrorMsg(err));
            });
    };

    const renderDialogContent = () => {
        return (
            <>
                <div id="turnstile-wrapper" />
                {errorMsg && (
                    <div className="captcha-error-msg" title={errorMsg}>
                        {errorMsg}
                    </div>
                )}
            </>
        );
    };

    if (isMobile) {
        return (
            <ModalMask visible={visible} maskStyle={{ background: 'rgba(0, 0, 0, .25)' }}>
                <div className="fx-mobile-turnstile-dialog">
                    <div
                        className={classNames('dialog-content', {
                            error: errorMsg
                        })}
                    >
                        <div className="captcha-title">{platformIntl.get('captcha.title')}</div>
                        {renderDialogContent()}
                    </div>
                    <div className="dialog-footer">
                        <div className="dialog-btn cancel" onClick={onClose}>
                            {platformIntl.get('cancel')}
                        </div>
                    </div>
                </div>
            </ModalMask>
        );
    } else {
        return (
            <Dialog
                className="fx-turnstile-dialog"
                visible={visible}
                separator={false}
                width={350}
                height={errorMsg ? 190 : 180}
                title={platformIntl.get('captcha.title')}
                onCancel={onClose}
                text4Ok={null}
                text4Cancel={null}
            >
                {renderDialogContent()}
            </Dialog>
        );
    }
};

export default TurnstileDialog;
